import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Loader } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import useSharedStyles from "./sharedStyles";
import ReportsListView from "./views/ReportsListView";
import PastDueView from "./views/PastDueView";
import FailedReportsView from "./views/FailedReportsView";
import SettingsView from "./views/SettingsView";
import ReviewDetailView from "./views/ReviewDetailView";
import GenerateDrawer from "../../../components/QuarterlyReportDrawer/GenerateDrawer";
import SignatureModal from "../../../components/QuarterlyReportDrawer/SignatureModal";
import PdfPreviewModal from "../../../components/QuarterlyReportDrawer/PdfPreviewModal";
import ConfirmModal from "./components/ConfirmModal";
import { generateReportPdf } from "../../../components/QuarterlyReportDrawer/pdfExport";
import { providerLabel } from "../../../components/QuarterlyReportDrawer/components/ProviderPicker";
import { searchSkypointProviders } from "../../../services/skypointProvider";
import { blankManualDraft } from "./utils";
import { setLoading } from "../../../store/reducers/quarterlyReport";
import {
  getQuarterlyReports,
  getPastDueReports,
  getFailedReports,
  getReportSettings,
  updateReportSettings,
  retryFailedReport,
  getQuarterlyReportDraft,
  getReportForExport,
  saveQuarterlyReportDraft,
  signQuarterlyReport,
  deleteQuarterlyReport,
  uploadReportEvidence,
  uploadEvidenceFile,
  registerReportEvidence,
  deleteReportEvidence,
  createManualReport,
} from "../../../services/quarterlyReport";

const SEARCH_DEBOUNCE_MS = 300;

const QuarterlyReportsPage = () => {
  const { profile } = useSelector((state) => state?.profile);
  const { classes } = useSharedStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    reports,
    totalReports,
    kpi,
    loading,
    pastDue,
    pastDueLoading,
    failed,
    failedLoading,
    settings,
    settingsLoading,
    draft,
    draftLoading,
    draftSaving,
  } = useSelector((state) => state.quarterlyReport);

  // Manual reports have no wizard anymore — a provider is a normal editable
  // field on the report itself, same as the individual's name. Creation just
  // needs a legitimate starting provider (the schema requires one, and every
  // report is provider-scoped/permission-gated), so this is the same
  // "Available for" eligibility the old drawer computed, now used to pick a
  // sensible default rather than to populate a picker step.
  // Eligible means approved for this contractor and of this kind ("" for both), searched on
  // the server: this used to filter the first page of providers (10) in the browser, so an
  // eligible provider past it could never be picked (OD-1694).
  const manualProviderType =
    { external: "external", ogiso: "ogiso" }[settings?.manualAvailableFor || "external"] || "";
  const manualProviders = (search = "") =>
    searchSkypointProviders(search, { approval: "approved", providerType: manualProviderType });

  const [view, setView] = useState("list");

  const [listStatus, setListStatus] = useState("all");
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [presetProviderId, setPresetProviderId] = useState(null);
  const [presetProviderName, setPresetProviderName] = useState("");

  const [batch, setBatch] = useState([]); // array of reportIds
  const [batchIndex, setBatchIndex] = useState(0);
  const [localDraft, setLocalDraft] = useState(null);

  const [signOpen, setSignOpen] = useState(false);
  const [pendingAdvance, setPendingAdvance] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const [toast, setToast] = useState(null);

  // Declared (and therefore run) before the deep-link effect below on purpose:
  // both fire in the same pass on mount, and effects run in declaration order,
  // so the deep-link's setLocalDraft(blankManualDraft(...)) must be the last
  // write to win. If this ran second, it would stomp the fresh manual draft
  // with whatever stale `draft` Redux still held from a previous visit to
  // this page (or null), leaving `view` on "detail" but `localDraft` empty —
  // which renders nothing at all.
  useEffect(() => {
    setLocalDraft(draft);
  }, [draft]);

  // A provider's "New manual report" action deep-links here. Consumed once and
  // cleared from history so a back/forward navigation doesn't recreate a report.
  useEffect(() => {
    if (!location.state?.createManual) return;
    handleCreateManual(location.state.providerId || null, location.state.providerName || "");
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.createManual]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1800);
    return () => clearTimeout(t);
  }, [toast]);

  // Only typing is debounced — a page change or a filter toggle refetches at
  // once, even while a search is active. Trimmed before debouncing so trailing
  // whitespace is not its own query, and clearing the box skips the wait
  // entirely: an empty search is the reset every other path (goList, the
  // status toggle) goes through, and it must not sit 300ms behind them.
  const trimmedSearch = listSearch.trim();
  const [debouncedSearch] = useDebouncedValue(trimmedSearch, SEARCH_DEBOUNCE_MS);
  const listSearchQuery = trimmedSearch === "" ? "" : debouncedSearch;
  const searchPending = trimmedSearch !== listSearchQuery;

  // Server-driven pagination/filter/search — page, status and search always
  // come from this state, never hardcoded, so page 2+ and non-"all" filters
  // actually reflect the full backend result set (not just page 1's rows).
  //
  // Loading is flagged on every fetch, not just the first: a row's status
  // changes server-side (sign, delete, retry) while the previous page of rows
  // is still on screen, so the rows have to read as "being refreshed" until
  // the new statuses land. The first load gets the full-page loader; later
  // ones get ReportsListView's overlay, which keeps the layout in place.
  const [listLoadedOnce, setListLoadedOnce] = useState(false);
  // Same fact as listLoadedOnce, kept in a ref because the effect only reads
  // it — as a dependency it would refire the fetch the moment it flipped.
  const listLoadedOnceRef = useRef(false);
  useEffect(() => {
    if (view !== "list") return;
    // Refreshes only: the rows already on screen stay up, so the overlay has to
    // be flagged before they repaint. The very first load has nothing to keep
    // and lets the fetch itself raise the flag.
    if (listLoadedOnceRef.current) dispatch(setLoading(true));
    let cancelled = false;
    (async () => {
      await getQuarterlyReports(listPage, listStatus, listSearchQuery);
      if (cancelled) return;
      listLoadedOnceRef.current = true;
      setListLoadedOnce(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [view, listPage, listStatus, listSearchQuery, dispatch]);

  const changeListStatus = (status) => {
    setListStatus(status);
    setListPage(1);
  };
  const changeListSearch = (search) => {
    setListSearch(search);
    setListPage(1);
  };

  // Fetched once up front (not just when Settings is opened) so the review
  // screen knows whether a finalized report should render read-only, per the
  // "Lock reports after finalizing" setting.
  useEffect(() => {
    getReportSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === "worklist") getPastDueReports();
    if (view === "failed") getFailedReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const currentReportId = batch[batchIndex];

  // Opening a different report drops the previous one's draft first, so the
  // review screen shows its loader instead of painting the last report's
  // content — and its status pill — until the new one arrives. A manual report
  // that was just created keeps the draft already in hand (its id is the one
  // being loaded), so nothing typed is thrown away mid-edit.
  useEffect(() => {
    if (!currentReportId) return;
    setLocalDraft((prev) => (prev?.id === currentReportId ? prev : null));
    getQuarterlyReportDraft(currentReportId).then((loaded) => {
      // The service already surfaced the error toast; without this the review
      // screen would sit blank now that the previous report is not kept up.
      if (!loaded) goList();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReportId]);

  const openGenerate = (providerId, providerName) => {
    setPresetProviderId(providerId || null);
    setPresetProviderName(providerName || "");
    setDrawerOpen(true);
  };

  // Coming back from the review screen the row that was just signed is still
  // in the list with its old status, so flag the refresh before the list
  // renders again — the reset-filters branch only fetches on the next effect
  // run, which would otherwise paint one frame of stale statuses first.
  const goList = () => {
    setView("list");
    dispatch(setLoading(true));
    if (listPage === 1 && listStatus === "all" && listSearch === "") {
      getQuarterlyReports(1, "all", "");
    } else {
      setListPage(1);
      setListStatus("all");
      setListSearch("");
    }
  };

  // A freshly created manual report opens straight into the review screen —
  // it is empty by design, and every field (including the provider) is filled
  // in there rather than in a separate wizard.
  const openSingleReport = (reportId) => {
    setBatch([reportId]);
    setBatchIndex(0);
    setView("detail");
  };

  // No create step and nothing hits the server yet: build a blank, local-only
  // draft (same shape the server would return) and open it directly in the
  // review screen.
  //
  // The provider comes preselected in exactly two cases: this came from a
  // specific provider's own "New manual report" action (a choice already made),
  // or there is only one eligible provider, in which case there is nothing to
  // choose and making the reviewer open a one-item dropdown is busywork. With
  // two or more it stays empty — picking one would be a guess — and the report
  // cannot be saved until the reviewer selects one (see ensureReportCreated).
  const handleCreateManual = async (presetProviderId, presetProviderName = "") => {
    let provider = presetProviderId ? { id: presetProviderId, label: presetProviderName } : null;
    if (!provider) {
      const { providers, count } = await manualProviders();
      if (count === 0) {
        setToast("Add an eligible provider first — see Report Settings › Manual Reports.");
        return;
      }
      if (count === 1) provider = { id: providers[0].id, label: providerLabel(providers[0]) };
    }
    setBatch([null]);
    setBatchIndex(0);
    setLocalDraft(blankManualDraft(provider?.id || null, provider?.label || ""));
    setView("detail");
  };

  // The manual create step no longer hits the server, so a report only really
  // exists once this has run — every Save/Sign path calls it first, and it is
  // a no-op once `localDraft.id` is set.
  const ensureReportCreated = async () => {
    if (localDraft?.id) return localDraft.id;
    if (!localDraft?.providerId) {
      setToast("Select a provider before saving.");
      return null;
    }
    const created = await createManualReport({
      providerId: localDraft.providerId,
      clientName: localDraft.clientName,
      facilityName: localDraft.facilityName,
      startDate: localDraft.periodStart,
      endDate: localDraft.periodEnd,
    });
    if (!created?.id) return null;

    // Evidence attached before the report existed was only ever uploaded to
    // S3 (see handleUploadEvidence) — register each one against the record
    // that now exists. Sequential on purpose: each call returns the report's
    // full evidenceFiles list, and the last response is the one kept.
    const pending = (localDraft.evidenceFiles || []).filter((f) => f.pending);
    let finalEvidenceFiles = created.evidenceFiles;
    for (const file of pending) {
      const updated = await registerReportEvidence(created.id, {
        name: file.name,
        fileUrl: file.fileUrl,
        fileType: file.fileType,
        fileSize: file.fileSize,
      });
      if (updated) finalEvidenceFiles = updated.evidenceFiles;
    }

    setBatch([created.id]);
    setBatchIndex(0);
    setLocalDraft((prev) => (prev ? { ...prev, id: created.id, evidenceFiles: finalEvidenceFiles } : prev));
    return created.id;
  };

  const handleReviewReports = (reportIds, startReportId) => {
    setBatch(reportIds);
    setBatchIndex(Math.max(0, reportIds.indexOf(startReportId)));
    setView("detail");
  };

  const changeCurrentDraft = (updater) => {
    setLocalDraft((prev) => updater(prev));
  };

  const advanceBatch = (isLast) => {
    if (isLast) {
      goList();
    } else {
      setBatchIndex((i) => i + 1);
    }
  };

  const isLocked = localDraft?.status === "final" && !!settings?.lockAfterFinalize;

  // AI-drafted reports only — a manual report's Save button is handleSaveDraft
  // instead (see ReviewDetailView's toolbar), so this never runs for one and
  // stays exactly as it was before manual reports existed.
  const handleSaveNext = (isLast) => {
    // Finalized + locked reports are read-only — nothing to save, just move on.
    if (isLocked) {
      advanceBatch(isLast);
      return;
    }
    if (!localDraft?.signature) {
      // Persist the edits BEFORE asking for a signature: signing posts only the
      // signature, so opening the modal straight from here would have thrown
      // away everything typed since the last save — the whole document on a
      // manual report, where every field is hand-entered.
      saveQuarterlyReportDraft(currentReportId, localDraft, () => {
        setPendingAdvance(true);
        setSignOpen(true);
      });
      return;
    }
    saveQuarterlyReportDraft(currentReportId, localDraft, () => {
      setToast("Changes saved");
      advanceBatch(isLast);
    });
  };

  // Manual reports are written over several sittings, so they get an explicit
  // save that doesn't imply finishing or signing.
  const handleSaveDraft = async () => {
    if (isLocked) return;
    const reportId = await ensureReportCreated();
    if (!reportId) return;
    saveQuarterlyReportDraft(reportId, { ...localDraft, id: reportId }, () => setToast("Draft saved"));
  };

  // Evidence lives on the report, not in the encrypted body, so these responses
  // are merged field-by-field rather than replacing localDraft — otherwise an
  // upload mid-edit would discard every unsaved section change.
  //
  // A report can be attached to before it exists (the reviewer may reach for
  // the source material before typing anything else) — that case only
  // uploads to S3 and holds the file locally; ensureReportCreated() registers
  // it against the real record on the first Save.
  const handleUploadEvidence = async (file) => {
    if (!currentReportId) {
      const fileMeta = await uploadEvidenceFile(file, settings?.manualEvidenceTypes);
      if (fileMeta) {
        setLocalDraft((prev) =>
          prev
            ? {
                ...prev,
                evidenceFiles: [...(prev.evidenceFiles || []), { ...fileMeta, id: `pending-${Date.now()}`, pending: true }],
              }
            : prev
        );
        setToast("Evidence attached");
      }
      return;
    }
    const updated = await uploadReportEvidence(currentReportId, file, settings?.manualEvidenceTypes);
    if (updated) {
      setLocalDraft((prev) => (prev ? { ...prev, evidenceFiles: updated.evidenceFiles } : prev));
      setToast("Evidence attached");
    }
  };

  const handleRemoveEvidence = async (evidenceId) => {
    // Not registered anywhere yet — just drop it locally (best-effort; the
    // orphaned S3 object is a minor cleanup gap, not a correctness issue,
    // same tradeoff already made for the registered-evidence delete path).
    if (!currentReportId || String(evidenceId).startsWith("pending-")) {
      setLocalDraft((prev) =>
        prev ? { ...prev, evidenceFiles: (prev.evidenceFiles || []).filter((f) => f.id !== evidenceId) } : prev
      );
      return;
    }
    const updated = await deleteReportEvidence(currentReportId, evidenceId);
    if (updated) {
      setLocalDraft((prev) => (prev ? { ...prev, evidenceFiles: updated.evidenceFiles } : prev));
      setToast("Evidence removed");
    }
  };

  // Set when the server answers 409 because the report still carries flagged
  // selections — holds the signature so it can be re-sent once confirmed.
  const [pendingAcknowledge, setPendingAcknowledge] = useState(null);

  const submitSignature = (sig) => {
    signQuarterlyReport(
      currentReportId,
      sig,
      (signedDraft) => {
        setLocalDraft(signedDraft);
        setSignOpen(false);
        setPendingAcknowledge(null);
        setToast("Signed & saved");
        if (pendingAdvance) {
          setPendingAdvance(false);
          advanceBatch(batchIndex === batch.length - 1);
        }
      },
      () => setPendingAcknowledge(sig)
    );
  };

  const handleSignConfirm = (sig) => submitSignature(sig);

  const confirmAcknowledgeFlags = () =>
    submitSignature({ ...pendingAcknowledge, acknowledgeFlags: true });

  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const confirmDeleteReport = async () => {
    const report = pendingDelete;
    if (!report) return;
    setDeletingId(report.id);
    // deleteQuarterlyReport swallows its own errors (already surfaced via
    // showError) and never rejects, so this await always resolves — the
    // callback only fires on a real 200, but deletingId must clear either way
    // or a failed delete would leave that row's action spinning forever.
    // The callback isn't awaited by the service, so the refetch is driven from
    // here instead — the row action has to keep spinning until the list without
    // that report is actually on screen.
    let deleted = false;
    await deleteQuarterlyReport(report.id, () => {
      deleted = true;
    });
    if (deleted) {
      await getQuarterlyReports(listPage, listStatus, listSearch);
      setToast("Report deleted");
    }
    setDeletingId(null);
    setPendingDelete(null);
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const handleOpenReport = async (report, opts) => {
    if (opts?.download) {
      setDownloadingId(report.id);
      const full = await getReportForExport(report.id);
      if (full) await generateReportPdf(full);
      setDownloadingId(null);
      return;
    }
    openSingleReport(report.id);
  };

  // Retries the failed report in place (POST /failed/:id/retry redrafts it
  // synchronously server-side) — this no longer opens a whole new Generate
  // wizard the user would have to fill in from scratch; it just re-runs the
  // draft for this exact client/period and reports the real outcome.
  const [retryingId, setRetryingId] = useState(null);
  const handleRetryFailed = async (row) => {
    setRetryingId(row.id);
    const result = await retryFailedReport(row.id);
    if (result) {
      if (view === "list") await getQuarterlyReports(listPage, listStatus, listSearch);
      setToast("Report regenerated — now Awaiting Review");
    }
    setRetryingId(null);
    // on failure retryFailedReport's own catch already surfaced the error toast;
    // getFailedReports(false) inside the service also already refreshed this list
  };

  return (
    <Box className={classes.container}>
      <AppNavbar title="Reports" profile={profile} />

      {view === "list" &&
        (loading && !listLoadedOnce ? (
          <Flex className={classes.centeredLoader}>
            <Loader color="violet" />
          </Flex>
        ) : (
          <ReportsListView
            reports={reports || []}
            totalReports={totalReports}
            status={listStatus}
            search={listSearch}
            page={listPage}
            onStatusChange={changeListStatus}
            onSearchChange={changeListSearch}
            onPageChange={setListPage}
            kpi={kpi}
            onGenerate={() => openGenerate(null)}
            onCreateManual={() => handleCreateManual(null)}
            manualEnabled={settings?.allowManualReports !== false}
            onOpenSettings={() => setView("settings")}
            onOpenPastDue={() => setView("worklist")}
            onOpenFailed={() => setView("failed")}
            onOpenReport={handleOpenReport}
            onDeleteReport={setPendingDelete}
            deletingId={deletingId}
            downloadingId={downloadingId}
            // searchPending covers the debounce window too, so the first
            // keystroke already reads as "searching" instead of leaving rows
            // that no longer match what is in the box.
            refreshing={loading || searchPending}
          />
        ))}

      {view === "worklist" &&
        (pastDueLoading && !pastDue?.length ? (
          <Flex className={classes.centeredLoader}>
            <Loader color="violet" />
          </Flex>
        ) : (
          <PastDueView
            rows={pastDue || []}
            onBack={goList}
            onGenerate={() => openGenerate(null)}
            onOpenSettings={() => setView("settings")}
            onStartReport={(r) => openGenerate(r.providerId, r.provider)}
          />
        ))}

      {view === "failed" &&
        (failedLoading && !failed?.length ? (
          <Flex className={classes.centeredLoader}>
            <Loader color="violet" />
          </Flex>
        ) : (
          <FailedReportsView
            rows={failed || []}
            retryingId={retryingId}
            onBack={goList}
            onGenerate={() => openGenerate(null)}
            onOpenSettings={() => setView("settings")}
            onRetry={handleRetryFailed}
          />
        ))}

      {view === "settings" &&
        (settingsLoading && !settings ? (
          <Flex className={classes.centeredLoader}>
            <Loader color="violet" />
          </Flex>
        ) : (
          <SettingsView
            onBack={goList}
            settings={settings || {}}
            onChange={updateReportSettings}
            saving={settingsLoading}
          />
        ))}

      {view === "detail" &&
        (draftLoading && !localDraft ? (
          <Flex className={classes.centeredLoader}>
            <Loader color="violet" />
          </Flex>
        ) : (
          localDraft && (
            <ReviewDetailView
              batch={batch}
              index={batchIndex}
              draft={localDraft}
              locked={localDraft.status === "final" && !!settings?.lockAfterFinalize}
              saving={draftSaving}
              evidenceTypes={settings?.manualEvidenceTypes}
              manualProviderType={manualProviderType}
              onChangeDraft={changeCurrentDraft}
              onPrev={() => setBatchIndex((i) => Math.max(0, i - 1))}
              onNext={() => setBatchIndex((i) => Math.min(batch.length - 1, i + 1))}
              onBack={goList}
              onSaveNext={handleSaveNext}
              onSaveDraft={handleSaveDraft}
              onUploadEvidence={handleUploadEvidence}
              onRemoveEvidence={handleRemoveEvidence}
              onOpenPdfPreview={() => setPdfOpen(true)}
              onOpenSign={async () => {
                // A manual report may not even be created yet (see
                // ensureReportCreated) — it has to save before this can open.
                // An AI-drafted report always already exists by the time it
                // reaches this screen, so it keeps its original behavior
                // untouched: open the modal directly, exactly as before.
                if (localDraft?.source !== "manual") {
                  setPendingAdvance(false);
                  setSignOpen(true);
                  return;
                }
                const reportId = await ensureReportCreated();
                if (!reportId) return;
                saveQuarterlyReportDraft(reportId, { ...localDraft, id: reportId }, () => {
                  setPendingAdvance(false);
                  setSignOpen(true);
                });
              }}
              onGenerate={() => openGenerate(null)}
              onOpenSettings={() => setView("settings")}
            />
          )
        ))}

      <GenerateDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          // The drawer's own generation batch runs server-side and can create
          // failed reports (KPI count, Failed Reports rows) after this list's
          // data was already fetched — closing the drawer doesn't re-mount
          // ReportsListView, so without this the KPI/table stay stale until a
          // manual page reload.
          getQuarterlyReports(listPage, listStatus, listSearch);
        }}
        initialProviderId={presetProviderId}
        initialProviderName={presetProviderName}
        onReviewReports={handleReviewReports}
      />

      <SignatureModal
        opened={signOpen}
        onClose={() => setSignOpen(false)}
        onConfirm={handleSignConfirm}
        requireTitle={!!settings?.requireTitle}
        saving={draftSaving}
      />

      <PdfPreviewModal opened={pdfOpen} onClose={() => setPdfOpen(false)} draft={localDraft} />

      {/* Worded as a confirmation, not an error: flags recompute only on
          generate/retry, so acknowledging them is the normal way a flagged
          report gets signed. */}
      <ConfirmModal
        opened={!!pendingAcknowledge}
        onClose={() => setPendingAcknowledge(null)}
        onConfirm={confirmAcknowledgeFlags}
        title="Confirm the flagged selections"
        message={
          `${localDraft?.flags?.length || 0} selection${
            (localDraft?.flags?.length || 0) === 1 ? "" : "s"
          } on this report could not be evidenced from the documentation and were left for you to decide. ` +
          "Signing records that you reviewed and accept them as they now stand."
        }
        confirmLabel="Confirm & sign"
        busyLabel="Signing…"
        busy={draftSaving}
      />

      <ConfirmModal
        opened={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDeleteReport}
        title="Delete this report?"
        message={
          pendingDelete
            ? `The report for ${pendingDelete.client} (${pendingDelete.period}) will be permanently deleted. This cannot be undone.`
            : null
        }
        confirmLabel="Delete report"
        busyLabel="Deleting…"
        busy={!!deletingId}
        danger
      />

      {toast && <Box className={classes.toast}>{toast}</Box>}
    </Box>
  );
};

export default QuarterlyReportsPage;
